import React, { useState } from 'react';
import axios from 'axios';

const RelatorioTurma = ({ API, getAuthHeaders, setCurrentView, turmas }) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedTurma, setSelectedTurma] = useState('');
  const [periodoTipo, setPeriodoTipo] = useState('trimestre');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [relatorio, setRelatorio] = useState(null);

  const calcularPeriodo = (tipo) => {
    const hoje = new Date();
    let start, end;

    if (tipo === 'trimestre') {
      start = new Date(hoje);
      start.setMonth(start.getMonth() - 3);
      end = hoje;
    } else if (tipo === 'semestre') {
      start = new Date(hoje);
      start.setMonth(start.getMonth() - 6);
      end = hoje;
    } else if (tipo === 'ano') {
      start = new Date(hoje);
      start.setFullYear(start.getFullYear() - 1);
      end = hoje;
    } else {
      return { start: startDate, end: endDate };
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const gerarRelatorio = async () => {
    if (!selectedTurma) {
      alert('Selecione uma turma');
      return;
    }

    const periodo = calcularPeriodo(periodoTipo);
    if (!periodo.start || !periodo.end) {
      alert('Defina o período');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/frequencia-turma?turma_id=${selectedTurma}&start=${periodo.start}&end=${periodo.end}`,
        { headers: getAuthHeaders() }
      );
      setRelatorio(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao gerar relatório');
    }
    setLoading(false);
  };

  const baixarXLSX = async () => {
    if (!relatorio) return;

    const periodo = calcularPeriodo(periodoTipo);
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/reports/frequencia-turma/xlsx?turma_id=${selectedTurma}&start=${periodo.start}&end=${periodo.end}`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'frequencia.xlsx';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Erro ao baixar arquivo');
    }
    setDownloading(false);
  };

  const formatarData = (dataStr) => {
    const [ano, mes, dia] = dataStr.split('-');
    return `${dia}/${mes}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-900 via-blue-800 to-sky-900 text-white px-4 sm:px-6 py-4 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="self-start px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-200 text-sm font-medium touch-manipulation"
          >
            ← Voltar ao Dashboard
          </button>

          <div className="text-center sm:flex-1 sm:mx-6">
            <h1 className="text-xl sm:text-2xl font-bold">📋 Frequência por Turma</h1>
            <p className="text-sky-200 text-sm mt-1 hidden sm:block">Grade detalhada de presença - aluno x domingo</p>
          </div>

          <div className="text-right text-sm hidden sm:block">
            <p>Sistema EBD</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-4 px-4">
        {/* Filtros */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">🔍 Filtros do Relatório</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Turma */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Turma</label>
              <select
                value={selectedTurma}
                onChange={(e) => setSelectedTurma(e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione uma turma</option>
                {turmas.filter(t => t.ativa !== false).map(turma => (
                  <option key={turma.id} value={turma.id}>{turma.nome}</option>
                ))}
              </select>
            </div>

            {/* Período Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Período</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'trimestre', label: '3 Meses' },
                  { value: 'semestre', label: '6 Meses' },
                  { value: 'ano', label: '1 Ano' },
                  { value: 'personalizado', label: 'Custom' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setPeriodoTipo(opt.value)}
                    className={`px-2 py-2 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                      periodoTipo === opt.value
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Datas Personalizadas */}
            {periodoTipo === 'personalizado' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Inicial</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Final</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}

            {/* Botão Gerar */}
            <div className={`flex items-end ${periodoTipo !== 'personalizado' ? 'sm:col-span-2 lg:col-span-2' : ''}`}>
              <button
                onClick={gerarRelatorio}
                disabled={loading}
                className="w-full px-4 py-3 text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors duration-200 font-medium touch-manipulation"
              >
                {loading ? '🔄 Carregando...' : '📊 Gerar Relatório'}
              </button>
            </div>
          </div>
        </div>

        {/* Resultado */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-4xl mb-4">🔄</div>
            <p className="text-gray-600">Carregando relatório de frequência...</p>
          </div>
        ) : relatorio ? (
          <>
            {/* Resumo */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">📈 Resumo - {relatorio.turma_nome}</h3>
                <button
                  onClick={baixarXLSX}
                  disabled={downloading}
                  className="mt-2 sm:mt-0 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors duration-200 font-medium text-sm touch-manipulation"
                >
                  {downloading ? '⏳ Gerando...' : '📥 Baixar XLS'}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600">{relatorio.resumo.total_alunos}</div>
                  <div className="text-sm sm:text-base text-blue-700 mt-1">Alunos</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-green-600">{relatorio.resumo.total_domingos}</div>
                  <div className="text-sm sm:text-base text-green-700 mt-1">Domingos</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-purple-600">{relatorio.resumo.media_presenca}%</div>
                  <div className="text-sm sm:text-base text-purple-700 mt-1">Média de Presença</div>
                </div>
              </div>
            </div>

            {/* Tabela Desktop */}
            <div className="bg-white rounded-lg shadow-lg mb-6">
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-10 min-w-[180px]">Aluno</th>
                      {relatorio.datas.map(d => (
                        <th key={d} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase min-w-[50px]">
                          {formatarData(d)}
                        </th>
                      ))}
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pres.</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Faltas</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">%</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {relatorio.alunos.map((aluno, index) => (
                      <tr key={aluno.aluno_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                          {aluno.nome}
                        </td>
                        {relatorio.datas.map(d => (
                          <td key={d} className="px-2 py-3 text-center">
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                              aluno.presencas[d] === 'P'
                                ? 'bg-emerald-500 text-white'
                                : aluno.presencas[d] === '-'
                                ? 'bg-gray-100 text-gray-400'
                                : 'bg-red-500 text-white'
                            }`}>
                              {aluno.presencas[d]}
                            </span>
                          </td>
                        ))}
                        <td className="px-3 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                            {aluno.total_presencas}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            {aluno.total_faltas}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                            aluno.percentual >= 75 ? 'bg-emerald-100 text-emerald-800' :
                            aluno.percentual >= 50 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {aluno.percentual}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden p-4 space-y-4">
                {relatorio.alunos.map((aluno) => (
                  <div key={aluno.aluno_id} className="border border-gray-200 rounded-lg p-4">
                    <div className="font-semibold text-gray-900 mb-3">{aluno.nome}</div>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="text-center bg-emerald-50 rounded-lg p-2">
                        <div className="text-lg font-bold text-emerald-600">{aluno.total_presencas}</div>
                        <div className="text-xs text-emerald-700">Presenças</div>
                      </div>
                      <div className="text-center bg-red-50 rounded-lg p-2">
                        <div className="text-lg font-bold text-red-600">{aluno.total_faltas}</div>
                        <div className="text-xs text-red-700">Faltas</div>
                      </div>
                      <div className="text-center bg-purple-50 rounded-lg p-2">
                        <div className={`text-lg font-bold ${
                          aluno.percentual >= 75 ? 'text-emerald-600' :
                          aluno.percentual >= 50 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>{aluno.percentual}%</div>
                        <div className="text-xs text-purple-700">Frequência</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {relatorio.datas.map(d => (
                        <span
                          key={d}
                          className={`inline-flex items-center justify-center w-8 h-6 rounded text-xs font-bold ${
                            aluno.presencas[d] === 'P'
                              ? 'bg-emerald-500 text-white'
                              : aluno.presencas[d] === '-'
                              ? 'bg-gray-100 text-gray-400'
                              : 'bg-red-500 text-white'
                          }`}
                          title={formatarData(d)}
                        >
                          {aluno.presencas[d]}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {relatorio.alunos.length === 0 && (
                <div className="p-8 text-center">
                  <div className="text-4xl mb-4">📭</div>
                  <p className="text-gray-600 text-lg">Nenhum aluno encontrado nesta turma</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-4xl mb-4">📋</div>
            <p className="text-gray-600 text-lg">Selecione uma turma e período para gerar o relatório</p>
            <p className="text-gray-500 text-sm mt-2">O relatório mostra a grade de presença de cada aluno por domingo</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatorioTurma;
